// Frontend API client for Myoan Tarot

import type {
  Topic,
  SpreadType,
  CreateReadingResponse,
  GetReadingResponse,
  GetHistoryResponse,
  RetryGenerationResponse,
  GenerateResultRequest,
  GenerateResultResponse,
  ApiError,
  AdminOverviewResponse,
  AdminReadingDetailResponse,
  AdminReadingFilters,
  AdminReadingListResponse,
  AdminUserListResponse,
  AdminUserReadingsResponse,
  AdminCouponListResponse,
  AdminCreateCouponsResponse,
  CouponRedeemResponse,
  CouponStatus,
  WalletSummaryResponse,
  WalletSpendResponse,
  WalletRechargeResponse,
  ShareTokenResponse,
} from './types';
import { csrfHeader } from './csrf';

const API_BASE = '/api';

/**
 * Custom error class for API errors
 */
class ApiErrorClass extends Error implements ApiError {
  code: string;
  status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Helper to handle fetch responses and errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorData: ApiError;
    try {
      const raw = await response.json() as ApiError | { error?: ApiError };
      errorData = (typeof raw === 'object' && raw && 'error' in raw && raw.error)
        ? raw.error
        : raw as ApiError;
      if (!errorData || !errorData.code) {
        errorData = {
          code: 'UNKNOWN_ERROR',
          message: `HTTP ${response.status}: ${response.statusText}`,
          status: response.status
        };
      }
    } catch {
      errorData = {
        code: 'UNKNOWN_ERROR',
        message: `HTTP ${response.status}: ${response.statusText}`,
        status: response.status
      };
    }
    throw new ApiErrorClass(errorData.code, errorData.message, errorData.status);
  }

  return response.json();
}

/**
 * Create a new tarot reading
 * @param topic - The topic for the reading (연애, 직장, 금전, 종합)
 * @param question - Optional user question (max 200 characters)
 * @returns Promise with readingId and paymentUrl
 */
export async function createReading(
  topic: Topic,
  question?: string,
  spread?: SpreadType
): Promise<CreateReadingResponse> {
  const response = await fetch(`${API_BASE}/createReading`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    body: JSON.stringify({ topic, question, spread })
  });

  return handleResponse<CreateReadingResponse>(response);
}

/**
 * Get a reading owned by the current session.
 * @param id - The reading ID
 * @returns Promise with full reading data
 */
export async function getReading(
  id: string
): Promise<GetReadingResponse> {
  const params = new URLSearchParams({ id });
  const response = await fetch(`${API_BASE}/getReading?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include'
  });

  return handleResponse<GetReadingResponse>(response);
}

export async function getSharedReading(token: string): Promise<GetReadingResponse> {
  const params = new URLSearchParams({ token });
  const response = await fetch(`${API_BASE}/sharedReading?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  return handleResponse<GetReadingResponse>(response);
}

/**
 * Get current user's reading history (cards-only summary).
 */
export async function getHistory(limit = 20): Promise<GetHistoryResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  const response = await fetch(`${API_BASE}/history?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include'
  });

  return handleResponse<GetHistoryResponse>(response);
}

/**
 * Retry generation for a failed reading
 * @param id - The reading ID
 * @returns Promise indicating success
 */
export async function retryGeneration(
  id: string
): Promise<RetryGenerationResponse> {
  const response = await fetch(`${API_BASE}/retryGeneration`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include',
    body: JSON.stringify({ readingId: id })
  });

  return handleResponse<RetryGenerationResponse>(response);
}

export async function rotateShareToken(
  id: string,
  includeQuestion = true
): Promise<ShareTokenResponse> {
  const response = await fetch(`${API_BASE}/readings/${encodeURIComponent(id)}/share/rotate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include',
    body: JSON.stringify({ includeQuestion })
  });

  return handleResponse<ShareTokenResponse>(response);
}

export async function disableShareToken(id: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/readings/${encodeURIComponent(id)}/share/disable`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include'
  });

  return handleResponse<{ success: boolean }>(response);
}

/**
 * Generate tarot interpretation result directly from selected cards.
 * Demo/result flow에서 서버 LLM을 호출할 때 사용한다.
 */
export async function generateResult(
  payload: GenerateResultRequest
): Promise<GenerateResultResponse> {
  const response = await fetch(`${API_BASE}/generateResult`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    body: JSON.stringify(payload)
  });

  return handleResponse<GenerateResultResponse>(response);
}

/**
 * Submit mock payment completion to webhook endpoint.
 */
export async function submitMockPayment(
  readingId: string,
  transactionId: string,
  spread?: SpreadType
): Promise<{ success: boolean; message?: string; generationStatus?: string }> {
  const response = await fetch(`${API_BASE}/paymentWebhook`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    body: JSON.stringify({ readingId, transactionId, spread })
  });

  return handleResponse<{ success: boolean; message?: string; generationStatus?: string }>(response);
}

export async function getWalletSummary(): Promise<WalletSummaryResponse> {
  const response = await fetch(`${API_BASE}/wallet`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include'
  });

  return handleResponse<WalletSummaryResponse>(response);
}

export async function spendWalletForReading(
  readingId: string,
  spread?: SpreadType
): Promise<WalletSpendResponse> {
  const response = await fetch(`${API_BASE}/wallet/spend`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include',
    body: JSON.stringify({ readingId, spread })
  });

  return handleResponse<WalletSpendResponse>(response);
}

export async function submitMockRecharge(
  packageId: string
): Promise<WalletRechargeResponse> {
  const response = await fetch(`${API_BASE}/wallet/mock-recharge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include',
    body: JSON.stringify({ packageId })
  });

  return handleResponse<WalletRechargeResponse>(response);
}

export async function redeemCouponCode(code: string): Promise<CouponRedeemResponse> {
  const response = await fetch(`${API_BASE}/coupons/redeem`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include',
    body: JSON.stringify({ code })
  });

  return handleResponse<CouponRedeemResponse>(response);
}

export type KakaoPayOrderType = 'wallet-recharge';

export interface KakaoPayReadyRequest {
  orderType: KakaoPayOrderType;
  packageId?: string;
  readingId?: string;
  spread?: SpreadType;
}

export interface KakaoPayReadyResponse {
  tid: string;
  orderId: string;
  nextRedirectPcUrl?: string;
  nextRedirectMobileUrl?: string;
  nextRedirectAppUrl?: string;
}

export async function startKakaoPayReady(
  payload: KakaoPayReadyRequest
): Promise<KakaoPayReadyResponse> {
  const response = await fetch(`${API_BASE}/payment/kakaopay/ready`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include',
    body: JSON.stringify(payload)
  });

  return handleResponse<KakaoPayReadyResponse>(response);
}

export interface KakaoPayApproveRequest {
  orderType: KakaoPayOrderType;
  readingId?: string;
  packageId?: string;
  orderId: string;
  amount: number;
  pgToken: string;
  tid: string;
  spread?: SpreadType;
}

export interface KakaoPayApproveResponse {
  success: boolean;
  message?: string;
  generationStatus?: string;
  transactionId?: string;
  balancePoints?: number;
  pointsCredited?: number;
  pointsSpent?: number;
  orderType?: KakaoPayOrderType;
}

export async function approveKakaoPayPayment(
  payload: KakaoPayApproveRequest
): Promise<KakaoPayApproveResponse> {
  const response = await fetch(`${API_BASE}/payment/kakaopay/approve`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include',
    body: JSON.stringify(payload)
  });

  return handleResponse<KakaoPayApproveResponse>(response);
}

function buildAdminQuery(filters: AdminReadingFilters): string {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  const query = params.toString();
  return query ? `?${query}` : '';
}

export async function getAdminOverview(): Promise<AdminOverviewResponse> {
  const response = await fetch(`${API_BASE}/admin/overview`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  return handleResponse<AdminOverviewResponse>(response);
}

export async function getAdminReadings(filters: AdminReadingFilters = {}): Promise<AdminReadingListResponse> {
  const response = await fetch(`${API_BASE}/admin/readings${buildAdminQuery(filters)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  return handleResponse<AdminReadingListResponse>(response);
}

export async function getAdminReadingDetail(id: string): Promise<AdminReadingDetailResponse> {
  const response = await fetch(`${API_BASE}/admin/readings/${encodeURIComponent(id)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  return handleResponse<AdminReadingDetailResponse>(response);
}

export async function retryAdminReading(id: string): Promise<RetryGenerationResponse> {
  const response = await fetch(`${API_BASE}/admin/readings/${encodeURIComponent(id)}/retry`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include'
  });

  return handleResponse<RetryGenerationResponse>(response);
}

export async function getAdminUsers(query?: string): Promise<AdminUserListResponse> {
  const params = new URLSearchParams();
  if (query) {
    params.set('query', query);
  }
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${API_BASE}/admin/users${suffix}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  return handleResponse<AdminUserListResponse>(response);
}

export async function getAdminUserReadings(id: string): Promise<AdminUserReadingsResponse> {
  const response = await fetch(`${API_BASE}/admin/users/${encodeURIComponent(id)}/readings`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  return handleResponse<AdminUserReadingsResponse>(response);
}

export async function getAdminCoupons(filters: {
  status?: CouponStatus | '';
  spread?: SpreadType | '';
  query?: string;
  limit?: number;
} = {}): Promise<AdminCouponListResponse> {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  const suffix = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${API_BASE}/admin/coupons${suffix}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  return handleResponse<AdminCouponListResponse>(response);
}

export async function createAdminCoupons(payload: {
  spread: SpreadType;
  issueMode: 'random' | 'manual';
  count?: number;
  code?: string;
}): Promise<AdminCreateCouponsResponse> {
  const response = await fetch(`${API_BASE}/admin/coupons`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include',
    body: JSON.stringify(payload)
  });

  return handleResponse<AdminCreateCouponsResponse>(response);
}

export async function disableAdminCoupon(couponId: string, reason?: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/coupons/${encodeURIComponent(couponId)}/disable`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include',
    body: JSON.stringify({ reason })
  });

  return handleResponse<{ success: boolean }>(response);
}
